import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type { Product, ProductInsert, ProductUpdate } from '../lib/database.types'
import { usePrints, usePrintBuilds } from './usePrints'
import { useSaleLineItems } from './useSaleLineItems'
import { useSales } from './useSales'
import { useBuilds } from './useBuilds'

const PRODUCTS_KEY = ['products']

export function useProducts() {
  return useQuery({
    queryKey: PRODUCTS_KEY,
    queryFn: async (): Promise<Product[]> => {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('name', { ascending: true })
      if (error) throw error
      return data ?? []
    },
  })
}

export function useCreateProduct() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: Omit<ProductInsert, 'user_id'>): Promise<Product> => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) throw new Error('not authenticated')
      const { data, error } = await supabase
        .from('products')
        .insert({ ...input, user_id: user.id })
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEY })
    },
  })
}

export function useUpdateProduct() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: { id: string } & ProductUpdate): Promise<Product> => {
      const { data, error } = await supabase
        .from('products')
        .update(updates)
        .eq('id', id)
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEY })
    },
  })
}

export function useDeleteProduct() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('products').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEY })
    },
  })
}

export interface ProductInventory {
  unitsProduced: number
  unitsSold: number
  inStock: number
  totalCostBasis: number
  avgUnitCost: number
  avgUnitPrice: number
  avgMargin: number
}

/**
 * Derives inventory stats for a product from cached builds + prints + sale_line_items.
 * Counts COMPLETED builds for inventory (not raw prints.units_produced).
 * Cost basis uses all prints linked to any build for this product.
 */
export function useProductInventory(productId: string | null | undefined): ProductInventory {
  const { data: prints } = usePrints()
  const { data: lineItems } = useSaleLineItems()
  const { data: sales } = useSales()
  const { data: builds } = useBuilds()
  const { data: printBuilds } = usePrintBuilds()

  return useMemo<ProductInventory>(() => {
    if (!productId) {
      return {
        unitsProduced: 0,
        unitsSold: 0,
        inStock: 0,
        totalCostBasis: 0,
        avgUnitCost: 0,
        avgUnitPrice: 0,
        avgMargin: 0,
      }
    }

    // Completed builds for this product — these are the sellable units
    const productBuilds = (builds ?? []).filter(
      (b) => b.product_id === productId && b.status === 'completed',
    )
    const unitsProduced = productBuilds.reduce((sum, b) => sum + b.units, 0)

    // Cost basis allocation: if a print contributes to N builds, split its cost
    // evenly across those builds. This makes multi-product plates accounted for
    // fairly (each product gets its share of the plate cost).
    const productBuildIds = new Set(
      (builds ?? [])
        .filter((b) => b.product_id === productId)
        .map((b) => b.id),
    )
    // Count how many builds each print is attached to via the junction.
    const buildsPerPrint = new Map<string, number>()
    for (const pb of printBuilds ?? []) {
      buildsPerPrint.set(pb.print_id, (buildsPerPrint.get(pb.print_id) ?? 0) + 1)
    }
    let totalCostBasis = 0
    for (const pb of printBuilds ?? []) {
      if (!productBuildIds.has(pb.build_id)) continue
      const print = (prints ?? []).find((p) => p.id === pb.print_id)
      if (!print) continue
      const shareCount = buildsPerPrint.get(pb.print_id) ?? 1
      totalCostBasis += Number(print.total_cost) / shareCount
    }
    const avgUnitCost = unitsProduced > 0 ? totalCostBasis / unitsProduced : 0

    // Sales side — exclude line items from refunded sales (units returned to stock).
    const refundedSaleIds = new Set(
      (sales ?? []).filter((s) => s.is_refunded).map((s) => s.id),
    )
    const relevantLineItems = (lineItems ?? [])
      .filter((li) => li.product_id === productId)
      .filter((li) => !refundedSaleIds.has(li.sale_id))
    const unitsSold = relevantLineItems.reduce((s, li) => s + li.quantity, 0)
    const revenue = relevantLineItems.reduce(
      (s, li) => s + li.quantity * Number(li.unit_price),
      0,
    )
    const avgUnitPrice = unitsSold > 0 ? revenue / unitsSold : 0
    const avgMargin = avgUnitPrice - avgUnitCost

    return {
      unitsProduced,
      unitsSold,
      inStock: unitsProduced - unitsSold,
      totalCostBasis,
      avgUnitCost,
      avgUnitPrice,
      avgMargin,
    }
  }, [productId, prints, lineItems, sales, builds, printBuilds])
}
